/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: tabs\DevicePhonebookTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Provisioning Devices module.
----------------------------------------------------------------*/

import { AlertCircle, RefreshCw, Edit, BookOpen } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import Combobox from '../../../components/ui/Combobox';
import { useI18n } from '../../../hooks/useI18n';
import { extractPhonebookInfo } from '../../provisioning-templates/utils/extractOptions';
import { PhonebookEntryConfig } from '../../provisioning-templates/types';
import type { DeviceModel } from '../../provisioning-device-models/api';
import type { Template } from '../../provisioning-templates/api';

interface DevicePhonebookTabProps {
  selectedModel: DeviceModel;
  selectedTemplate: Template | null;
  devicePhonebook: PhonebookEntryConfig[];
  setDevicePhonebook: React.Dispatch<React.SetStateAction<PhonebookEntryConfig[]>>;
  isPhonebookOverridden: boolean;
  setIsPhonebookOverridden: (val: boolean) => void;
  availablePhonebooks: Array<{ id?: number; uuid?: string; description: string; url?: string }>;
}

export function DevicePhonebookTab({
  selectedModel, selectedTemplate, devicePhonebook, setDevicePhonebook,
  isPhonebookOverridden, setIsPhonebookOverridden, availablePhonebooks
}: DevicePhonebookTabProps) {
  const { t } = useI18n();
  const pbInfo = extractPhonebookInfo(selectedModel.phonebook);
  const fields = Object.entries(pbInfo.fields || {});

  const updatePbEntry = (index: number, field: string, value: unknown) => {
    if (!isPhonebookOverridden) return;
    setDevicePhonebook(prev => {
      const updated = [...prev];
      while (updated.length <= index) updated.push({ id: `pb-auto-${updated.length}` });
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderFieldInput = (index: number, fieldKey: string, fieldConfig: Record<string, any>) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const currentEntry = devicePhonebook[index] as Record<string, any> || {};
    const value = currentEntry[fieldKey] !== undefined ? currentEntry[fieldKey] : '';
    const isDisabled = !isPhonebookOverridden;

    if (fieldKey === 'url') {
      const optionsWithCheck = availablePhonebooks.map(pb => ({ label: pb.description, value: pb.url || pb.uuid, checked: (pb.url || pb.uuid) === value }));
      return (
        <div className="flex items-center gap-1">
          {!isDisabled && (
            <div className="w-48 flex-none">
              <Combobox single options={optionsWithCheck as { value: string; label: string; checked: boolean }[]} onToggle={(val) => updatePbEntry(index, fieldKey, val)}
                placeholder={t('provisioningTemplates.selectPhonebook', 'Select Phonebook...')} className="w-full" />
            </div>
          )}
          <input type="text" value={value} onChange={(e) => updatePbEntry(index, fieldKey, e.target.value)}
            className="flex-1 px-2 py-1 border border-border rounded text-sm bg-background disabled:opacity-50"
            placeholder={t(`provisioningTemplates.${fieldKey}`, fieldKey)} disabled={isDisabled} />
        </div>
      );
    }
    if (fieldConfig.type === 'select' && fieldConfig.values) {
      const options = Object.entries(fieldConfig.values).map(([label, val]) => ({ label, value: String(val) }));
      return (
        <select value={value} onChange={(e) => updatePbEntry(index, fieldKey, e.target.value)}
          className="w-full px-2 py-1 border border-border rounded text-sm bg-background disabled:opacity-50" disabled={isDisabled}>
          <option value="">{t('common.select', 'Select')}</option>
          {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      );
    }
    if (fieldConfig.type === 'boolean') {
      return (
        <select value={String(value)} onChange={(e) => updatePbEntry(index, fieldKey, e.target.value === 'true')}
          className="w-full px-2 py-1 border border-border rounded text-sm bg-background disabled:opacity-50" disabled={isDisabled}>
          <option value="false">{t('common.no', 'No')}</option>
          <option value="true">{t('common.yes', 'Yes')}</option>
        </select>
      );
    }
    return (
      <input type={fieldConfig.type === 'password' ? 'password' : 'text'} value={value} onChange={(e) => updatePbEntry(index, fieldKey, e.target.value)}
        className="w-full px-2 py-1 border border-border rounded text-sm bg-background disabled:opacity-50"
        placeholder={t(`provisioningTemplates.${fieldKey}`, fieldKey)} disabled={isDisabled} />
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <div>
            <h4 className="font-semibold text-blue-900 dark:text-blue-100">
              {isPhonebookOverridden ? t('provisioningDevices.customPhonebook', 'Custom Phonebook') : t('provisioningDevices.templatePhonebook', 'Using Template Phonebook')}
            </h4>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              {isPhonebookOverridden ? t('provisioningDevices.customPhonebookDesc', 'This device has a specific phonebook.') : t('provisioningDevices.templatePhonebookDesc', 'Inheriting phonebook from template.')}
            </p>
          </div>
        </div>
        {isPhonebookOverridden ? (
          <button type="button" onClick={() => { setIsPhonebookOverridden(false); if (selectedTemplate?.phonebook) setDevicePhonebook(selectedTemplate.phonebook as PhonebookEntryConfig[]); }}
            className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-black text-foreground border rounded-md shadow-sm hover:bg-gray-50">
            <RefreshCw className="h-4 w-4" />{t('provisioningDevices.resetToTemplate', 'Reset to Template')}
          </button>
        ) : (
          <button type="button" onClick={() => setIsPhonebookOverridden(true)}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700">
            <Edit className="h-4 w-4" />{t('provisioningDevices.customize', 'Customize Phonebook')}
          </button>
        )}
      </div>
      <FormBox title={t('provisioningDevices.phonebook', 'Phonebook')} icon={<BookOpen className="h-4 w-4" />}>
        {pbInfo.count === 0 ? (
          <div className="text-center py-8 text-muted-foreground"><p>{t('provisioningTemplates.noPhonebook', 'This model does not support Phonebook configuration.')}</p></div>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-3 py-2 text-left font-medium w-16">#</th>
                  {fields.map(([key]) => (
                    <th key={key} className="px-3 py-2 text-left font-medium">
                      {t(`provisioningTemplates.${key}`, key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: pbInfo.count }).map((_, index) => (
                  <tr key={index} className="border-b border-border last:border-0 hover:bg-muted/30">
                    <td className="px-3 py-2 font-medium text-muted-foreground">{index + 1}</td>
                    {fields.map(([key, config]) => (
                      <td key={key} className="px-3 py-1">{renderFieldInput(index, key, config as Record<string, unknown>)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </FormBox>
    </div>
  );
}
